import { useEffect, useMemo, useState } from 'react';
import type { Workspace } from '@workspace/domain/workspace';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { ViewModelSharingUserRoleType } from '@view-model/domain/view-model';
import type { UserId, ViewModelIndividualPermissionRoleJSON } from 'balus-common';
import { RTDBPath } from 'balus-common';
import { useWorkspaceMemberRoles } from '@workspace/domain/workspace/useWorkspaceMemberRoles';
import { TextRepository, usePrimitiveValueListener } from '@framework/repository';

export type ViewModelPermissions = {
  isEditable: boolean;
  isContentEditable: boolean;
  /** AIアシスタントSkillの読み書きが可能か。ビューモデルのロック状態には依存しない。 */
  isSkillEditable: boolean;
  isDeletable: boolean;
};

const defaultPermissions = {
  isEditable: false,
  isContentEditable: false,
  isSkillEditable: false,
  isDeletable: false,
} satisfies ViewModelPermissions;

export const canEditViewModelSkill = ({
  isWorkspaceAdminOrEditor,
  individualPermission,
  individualPermissionsEnabled,
  isViewModelURLShareable,
  sharingUserRole,
}: {
  isWorkspaceAdminOrEditor: boolean;
  individualPermission: ViewModelIndividualPermissionRoleJSON | null;
  individualPermissionsEnabled: boolean;
  isViewModelURLShareable: boolean;
  sharingUserRole: ViewModelSharingUserRoleType;
}): boolean =>
  isWorkspaceAdminOrEditor ||
  (individualPermissionsEnabled && individualPermission === 'editor') ||
  (isViewModelURLShareable && sharingUserRole === ViewModelSharingUserRoleType.Editor);

/**
 * ビューモデルの編集・削除の可否を返す
 */
export const useViewModelPermissions = (
  currentUserId: UserId | null,
  workspace: Workspace,
  viewModel: ViewModelEntity | null
): ViewModelPermissions => {
  const [groupEnableIndividualViewModelPermissions] = usePrimitiveValueListener<boolean>(
    RTDBPath.Group.enableIndividualViewModelPermissionsPath(workspace.groupId)
  );
  const [individualPermission, setIndividualPermission] = useState<ViewModelIndividualPermissionRoleJSON | null>(null);

  useEffect(() => {
    if (!viewModel || !currentUserId) {
      setIndividualPermission(null);
      return;
    }

    const repo = new TextRepository<ViewModelIndividualPermissionRoleJSON>(
      RTDBPath.ViewModel.individualPermissionPath(viewModel.id, currentUserId)
    );
    repo.addListener((value) => setIndividualPermission(value));

    return () => repo.removeListener();
  }, [currentUserId, viewModel]);

  const roles = useWorkspaceMemberRoles(workspace.id);

  return useMemo(() => {
    if (currentUserId === null || viewModel === null) {
      return defaultPermissions;
    }

    // 対象ワークスペースに所属していて、管理者ロールまたは編集者ロールの場合には、編集・削除操作が可能
    const role = roles?.getRoleOf(currentUserId);
    const adminOrEditor = role && [WorkspaceMemberRole.admin, WorkspaceMemberRole.editor].includes(role);
    const isSkillEditable = canEditViewModelSkill({
      isWorkspaceAdminOrEditor: Boolean(adminOrEditor),
      individualPermission,
      individualPermissionsEnabled: Boolean(
        groupEnableIndividualViewModelPermissions && workspace.setting.enableIndividualViewModelPermissions
      ),
      isViewModelURLShareable: workspace.setting.isViewModelURLShareable,
      sharingUserRole: viewModel.sharingUserRole,
    });
    if (adminOrEditor) {
      return {
        isEditable: true,
        isContentEditable: true && !viewModel.isLocked,
        isSkillEditable,
        isDeletable: true,
      };
    }

    // ビューモデル個別権限の設定がグループとワークスペースで有効化されていて、個別に権限が付与されている場合
    if (
      groupEnableIndividualViewModelPermissions &&
      workspace.setting.enableIndividualViewModelPermissions &&
      individualPermission
    ) {
      return {
        isEditable: false,
        isContentEditable: individualPermission === 'editor' && !viewModel.isLocked,
        isSkillEditable,
        isDeletable: false,
      };
    }

    // 対象ワークスペースがURL共有許可されている場合は、ビューモデルの共有設定に従ってコンテンツ編集の可否が決まる
    if (workspace.setting.isViewModelURLShareable) {
      return {
        isEditable: false,
        isContentEditable: viewModel.sharingUserRole === ViewModelSharingUserRoleType.Editor && !viewModel.isLocked,
        isSkillEditable,
        isDeletable: false,
      };
    }

    // 公開ワークスペースの場合など、ビューモデルの編集・削除操作は行えない
    return {
      isEditable: false,
      isContentEditable: false,
      isSkillEditable,
      isDeletable: false,
    };
  }, [
    currentUserId,
    viewModel,
    roles,
    workspace.setting.isViewModelURLShareable,
    workspace.setting.enableIndividualViewModelPermissions,
    groupEnableIndividualViewModelPermissions,
    individualPermission,
  ]);
};
